import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  INTRO_ACCENT_COLOR,
  communicationIntroPNG,
  communicationIntroWEBP,
} from '~/pages/communication/intro/libs/components/IntroCommunication/constants'
import { getIntroTitleKeyByAdName } from '~/pages/communication/intro/libs/components/IntroCommunication/getIntroTitleKeyByAdName'
import { IntroCommunicationStyles as S } from '~/pages/communication/intro/libs/components/IntroCommunication/styles'
import { Progress } from '~/shared/components/Progress'
import { eventLogger } from '~/shared/services/eventLogger'
import { appHistory } from '~/shared/utils'

const LOADER_DURATION_MS = 3500
const TICK_MS = 100
const QUESTION = 'Speak Like a Leader'

interface IIntroCommunicationProps {
  nextPagePath: string
  pageId: string
}

const IntroCommunication = ({
  nextPagePath,
  pageId,
}: IIntroCommunicationProps) => {
  const [progressPercent, setProgressPercent] = useState(0)
  const { search } = useLocation()
  const adName = new URLSearchParams(search).get('ad_name') ?? ''
  const titleKey = getIntroTitleKeyByAdName(adName)

  useEffect(() => {
    let elapsed = 0

    const timer = setInterval(() => {
      elapsed += TICK_MS
      const percent = Math.min((elapsed / LOADER_DURATION_MS) * 100, 100)
      setProgressPercent(percent)

      if (elapsed >= LOADER_DURATION_MS) {
        clearInterval(timer)
        eventLogger.logFirstPageCompleted(QUESTION)
        eventLogger.logQuestion({
          question: QUESTION,
          pageName: pageId,
        })
        appHistory.goTo(nextPagePath)
      }
    }, TICK_MS)

    return () => clearInterval(timer)
  }, [nextPagePath])

  return (
    <S.Root>
      <S.ContentWrapper>
        <S.TitleGroup>
          <S.Title>
            <Trans
              ns="communication"
              i18nKey={`onboarding.communicationIntro.${titleKey}`}
            />
          </S.Title>

          <S.Subtitle>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationIntro.subtitle"
            />
          </S.Subtitle>
        </S.TitleGroup>

        <S.Badges>
          <S.Badge>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationIntro.quizBadge"
            />
          </S.Badge>
          <S.Badge>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationIntro.ratingBadge"
            />
          </S.Badge>
        </S.Badges>

        <picture>
          <source srcSet={communicationIntroWEBP} type="image/webp" />
          <S.Image src={communicationIntroPNG} alt="Speak Like a Leader" />
        </picture>

        <S.LoaderSection>
          <S.LoaderText>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationIntro.loaderText"
            />
          </S.LoaderText>

          <S.ProgressWrapper>
            <Progress
              color={INTRO_ACCENT_COLOR}
              $background="transparent"
              $valueBorderRadius="999px"
              $roundedAllSides
              value={progressPercent}
              max={100}
            />
          </S.ProgressWrapper>
        </S.LoaderSection>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default IntroCommunication
